import { RequestParams } from '../../models/enhancement'
import {
  CalendarDetailsResponse,
  CurrentEntitlementPeriodResponse,
  SelectOption,
} from '../../services/dashboardService'
import { FlattenedRequest } from '../../types/flattened-requests'
import { enhancementTypes } from '../../utils/constants'
import { CheckboxFilterSettings, SelectionState } from '../MyActions/MyActionsFilter/Types'
import { MOBILE_EXCLUDED_REQUEST_TYPES } from './Shared/enums'

export const getDateFrom = (
  currentEntitlementPeriodResponse: CurrentEntitlementPeriodResponse,
  calendarDetailsResponse: CalendarDetailsResponse
): Date => {
  const lastEntitlementPeriodYear =
    new Date(currentEntitlementPeriodResponse.startDate).getFullYear() - 1
  const foundCalendarDetail = calendarDetailsResponse.calendarDetails.find(
    detail => detail.year === lastEntitlementPeriodYear
  )

  if (foundCalendarDetail) {
    const date = new Date(
      new Date(foundCalendarDetail.startDate).getFullYear(),
      new Date(foundCalendarDetail.startDate).getMonth(),
      new Date(foundCalendarDetail.startDate).getDate()
    )
    return date
  }
  return new Date(
    new Date(currentEntitlementPeriodResponse.startDate).getFullYear() - 1,
    new Date(currentEntitlementPeriodResponse.startDate).getMonth(),
    new Date(currentEntitlementPeriodResponse.startDate).getDate()
  )
}

export const getDateTo = (calendarDetailsResponse: CalendarDetailsResponse): Date => {
  const date = new Date(
    new Date(
      calendarDetailsResponse.calendarDetails[
        calendarDetailsResponse.calendarDetails.length - 1
      ].endDate
    ).getFullYear(),
    new Date(
      calendarDetailsResponse.calendarDetails[
        calendarDetailsResponse.calendarDetails.length - 1
      ].endDate
    ).getMonth(),
    new Date(
      calendarDetailsResponse.calendarDetails[
        calendarDetailsResponse.calendarDetails.length - 1
      ].endDate
    ).getDate())
    return date
  }


export const getInitialRequestParams = (
  calendarDetailsResponse?: CalendarDetailsResponse,
  selectedDepartmentFilter?: SelectOption | null,
  currentEntitlementPeriodResponse?: CurrentEntitlementPeriodResponse
): RequestParams | undefined => {
  if (!currentEntitlementPeriodResponse || !calendarDetailsResponse) return
  const dateFrom = getDateFrom(currentEntitlementPeriodResponse, calendarDetailsResponse)
  const dateTo = getDateTo(calendarDetailsResponse)

  return {
    directReportees: true,
    dateFrom,
    dateTo,
    departmentId: selectedDepartmentFilter?.value,
    departmentTeamId: undefined,
    requestStatusId: 97,
    enhancementsOnly: false,
  }
}

export const getInitialSelectionStates = (
  calendarDetailsResponse?: CalendarDetailsResponse,
  selectedDepartmentFilter?: SelectOption | null,
  currentEntitlementPeriodResponse?: CurrentEntitlementPeriodResponse
): SelectionState | undefined => {
  if (!currentEntitlementPeriodResponse || !calendarDetailsResponse) return
  return {
    team: null,
    employees: null,
    status: 97,
    department: selectedDepartmentFilter,
    dateRange: [null, null],
    directReportees: true,
  }
}

export const getInitialCheckboxSettings = (
  enhancementsSettingOn?: boolean
): CheckboxFilterSettings[] => {
  let settings: CheckboxFilterSettings[] = [
    { type: 'Holiday', displayName: 'Holidays' },
    { type: 'Absence', displayName: 'Absences' },
    { type: 'Late', displayName: 'Late' },
    { type: 'Day Off', displayName: 'Days off' },
    { type: 'Work From Home', displayName: 'WFH' },
    { type: 'Lieu', displayName: 'Lieu' },
    { type: 'Shift', displayName: 'Shift' },
    { type: 'Birthday', displayName: 'Birthday' },
    { type: 'Sell', displayName: 'Sell' },
    { type: 'Buy', displayName: 'Buy' },
    { type: 'Adjustment', displayName: 'Adjustments' },
    { type: 'Holiday (M)', displayName: 'Holiday (M)' },
    { type: 'Lieu (M)', displayName: 'Lieu (M)' },
    { type: 'Course', displayName: 'Course' },
    { type: 'Paternity', displayName: 'Paternity' },
    { type: 'Maternity', displayName: 'Maternity' },
    { type: 'Twilight Cover', displayName: 'Twilight Cover' },
    { type: 'Night Shift Cover', displayName: 'Night Shift Cover' },
    { type: 'Wfm Absence', displayName: 'Wfm Absence' },
    { type: 'Other', displayName: 'Other' },
    { type: 'ManAuth', displayName: 'Discretionary Pay' },
  ]

  if (enhancementsSettingOn) {
    const enhancementsRequestFilters: CheckboxFilterSettings[] = [
      { type: enhancementTypes.overtime, displayName: 'Overtime' },
      { type: enhancementTypes.night, displayName: 'Nights' },
      { type: enhancementTypes.onCall, displayName: 'On Call' },
      { type: enhancementTypes.callOut, displayName: 'Call Outs' },
      { type: enhancementTypes.weekend, displayName: 'Weekend' },
    ]
    settings = [...settings, ...enhancementsRequestFilters]
  }

  settings.forEach(setting => {
    // eslint-disable-next-line no-param-reassign
    setting.checked = false
  })

  return settings
}

export const enhancementCheckboxSettings = (): CheckboxFilterSettings[] => {
  const enhancementsTypes = [
    enhancementTypes.overtime,
    enhancementTypes.night,
    enhancementTypes.onCall,
    enhancementTypes.callOut,
    enhancementTypes.weekend,
  ]

  const settings: CheckboxFilterSettings[] = getInitialCheckboxSettings(true).map(setting => ({
    ...setting,
    checked: enhancementsTypes.includes(setting.type),
  }))

  return settings
}

export const filter = (
  mobile: boolean,
  requestFilterSettings: CheckboxFilterSettings[],
  requests: FlattenedRequest[]
): FlattenedRequest[] => {
  let result

  const requestTypes: string[] = []

  requestFilterSettings.forEach(setting => {
    if (setting.checked) requestTypes.push(setting.type)
  })

  if (requestTypes.length === 0) {
    // When no filters are selected, show all requests on desktop
    // On mobile, show all non-absence related actions
    if (mobile) {
      result = requests.filter((request: FlattenedRequest) =>
        request.requestType ? !MOBILE_EXCLUDED_REQUEST_TYPES.includes(request.requestType) : true
      )
    } else {
      result = requests
    }
  } else {
    result = requests?.filter((request: FlattenedRequest) =>
      request.requestType ? requestTypes.includes(request.requestType) : false
    )

    // Even when specific filters are selected, mobile should never render
    // request types that are excluded from the mobile view (e.g. Absence).
    if (mobile) {
      result = result.filter((request: FlattenedRequest) =>
        request.requestType ? !MOBILE_EXCLUDED_REQUEST_TYPES.includes(request.requestType) : true
      )
    }
  }

  return result
}
